import { useCallback, useState } from "react";

import * as Sentry from "@sentry/react";
import { useMutation } from "react-relay";
import { type RecordSourceSelectorProxy } from "relay-runtime";

import { type RoleChanges } from "app/components/rbac/ACLFormFields";
import { UpdateRoleAssignmentMutation } from "app/mutations/UpdateRoleAssignmentMutation";

import type {
  UpdateRoleAssignmentMutation$data,
  UpdateRoleAssignmentMutation as UpdateRoleAssignmentMutationType,
} from "app/mutations/__generated__/UpdateRoleAssignmentMutation.graphql";

/** Encapsulates logic for setting role assignments */
export function useApplyRoleChanges(
  setFormErrors: (errors: string[]) => void,
  updater: (
    store: RecordSourceSelectorProxy<UpdateRoleAssignmentMutation$data>,
  ) => void,
) {
  const [commitRoleAssignment, isInFlightRoleAssignment] =
    useMutation<UpdateRoleAssignmentMutationType>(UpdateRoleAssignmentMutation);

  const [roleChanges, setRoleChanges] = useState<RoleChanges>({
    grants: [],
    revokes: [],
  });

  const handleRoleChanges = useCallback((changes: RoleChanges) => {
    setRoleChanges(changes);
  }, []);

  function applyRoleChanges(
    onSuccess?: () => void,
    roleChangesFromProps?: RoleChanges,
  ) {
    const roleChangesToApply = roleChangesFromProps || roleChanges;

    // If no changes, just call onSuccess
    if (
      roleChangesToApply.grants.length === 0 &&
      roleChangesToApply.revokes.length === 0
    ) {
      onSuccess?.();
      return;
    }

    commitRoleAssignment({
      variables: {
        input: {
          grant:
            roleChangesToApply.grants.length > 0
              ? roleChangesToApply.grants
              : undefined,
          revoke:
            roleChangesToApply.revokes.length > 0
              ? roleChangesToApply.revokes
              : undefined,
        },
        connections: [],
      },
      updater,
      onCompleted: (response, errors) => {
        if (errors?.length) {
          setFormErrors(errors.map((error) => error.message));
          return;
        }

        // Check for grant errors
        const grantErrors = response.updateRoleAssignment?.grant
          ?.filter((grant) => grant?.errorMessage)
          .map((grant) => grant?.errorMessage || "");

        // Check for revoke errors
        const revokeErrors = response.updateRoleAssignment?.revoke
          ?.filter((revoke) => revoke?.errorMessage)
          .map((revoke) => revoke?.errorMessage || "");

        // Combine errors
        const roleErrors = [...(grantErrors || []), ...(revokeErrors || [])];

        if (roleErrors.length > 0) {
          setFormErrors(roleErrors);
          return;
        }

        // Success
        onSuccess?.();
      },
      onError: (error) => {
        Sentry.captureException(error);
        setFormErrors([error.message]);
      },
    });
  }

  return {
    roleChanges,
    handleRoleChanges,
    applyRoleChanges,
    isInFlightRoleAssignment,
  };
}
/** When creating an object, we cannot set the role assignment target until we receive the response from the add mutation.
 * This must be used to fill in the target before calling appyRoleChanges fn */
export function setRoleAssignmentTarget(
  roleChanges: RoleChanges,
  target: string,
): RoleChanges {
  return {
    grants: roleChanges.grants.map((grant) => ({
      ...grant,
      target,
    })),
    revokes: roleChanges.revokes.map((revoke) => ({
      ...revoke,
      target,
    })),
  };
}
